import { NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    effect,
    ElementRef,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    Renderer2,
    RendererStyleFlags2,
    signal,
    untracked,
    viewChild
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { AttributeResource } from '@df/catalog/attribute/attribute.resource';
import { IProductDataV3 } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import { Url } from '@df/core/service/url';
import { resizeObservable } from '@df/dom/fn/resize-observable';
import { GridDirective } from '@df/ui/atomic/atom/grid/grid.directive';
import { OptionLabelPipe } from '@df/ui/common/option-label.pipe';
import { HideUpComponent } from '@df/ui/hide-up/hide-up.component';
import { ToggleModule } from '@df/ui/toggle/toggle.module';
import { ToggleService } from '@df/ui/toggle/toggle.service';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import { SwatchComponent } from '../../../../attribute/swatch/swatch.component';
import { IProductAttachment, Product } from '../../../product.decorator';

export namespace NProductViewDetailsLeft {
    export interface IAtributeItem {
        isText: boolean;
        isTextArea: boolean;
        label: string;
        code: string;
        valuesLabels: (string | number)[];
    }
}

let uid = 0;
@Component({
    selector: 'product-view-details-left',
    templateUrl: 'product-view-details-left.html',
    styleUrl: 'product-view-details-left.scss',
    imports: [TranslationModule, ToggleModule, HideUpComponent, SwatchComponent, OptionLabelPipe, GridDirective, NgTemplateOutlet],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductViewDetailsLeftComponent extends UiComponent implements OnChanges, OnDestroy {
    @Input({ required: true })
    product!: Product<IProductDataV3>;

    @Input()
    attributes?: string[];

    protected documents?: IProductAttachment[];
    protected technicalInformation?: [NProductViewDetailsLeft.IAtributeItem[], NProductViewDetailsLeft.IAtributeItem[]];
    protected readonly app = inject(App);
    private domSanitizer = inject(DomSanitizer);
    private urlService = inject(Url);
    private router = inject(Router);
    private renderer2 = inject(Renderer2);

    override async ngOnInit(): Promise<void> {
        super.ngOnInit();
        // the description is the primary copy of this column, so it starts open
        this.toggleService.open([this.uid, 'description']);
        this.detectChanges();
    }

    override ngOnChanges(changes: any): void {
        this.technicalInformation = this.getTechnicalInformation();
        this.documents = this.product.data?.attachments;
        this.safeDescription = this.getSafeDescription();
        this.descriptionExpanded.set(false);
        this.detectChanges();
    }

    protected readonly toggleService = inject(ToggleService);
    protected readonly uid = `product-view-details-left-${uid++}`;
    protected readonly attributeResource = inject(AttributeResource);

    /**
     * Description wrapper carrying the collapsed clamp
     */
    private readonly descriptionBody = viewChild<ElementRef<HTMLElement>>('descriptionBody');

    /**
     * Description copy at its natural height, measured against the clamp
     */
    private readonly descriptionContent = viewChild<ElementRef<HTMLElement>>('descriptionContent');

    /**
     * Whole description visible flag
     */
    protected readonly descriptionExpanded = signal(false);

    /**
     * Description runs past the clamp, so the read more control has something to reveal
     */
    protected readonly descriptionOverflows = signal(false);

    /**
     * Expand or collapse transition is running
     */
    protected readonly descriptionAnimating = signal(false);

    /**
     * Ties the read more button to the region it expands
     */
    protected readonly descriptionBodyId = `${this.uid}-description`;

    /**
     * Sanitized description, rebuilt when the product changes
     */
    protected safeDescription?: SafeHtml;

    /**
     * Re-measures whenever the description element appears or its copy reflows
     */
    private readonly descriptionResizeEffect = effect(onCleanup => {
        const content = this.descriptionContent()?.nativeElement;
        if (!content) {
            return;
        }
        this.measureDescription();
        const subscription = resizeObservable(content, () => this.measureDescription()).subscribe();
        onCleanup(() => subscription.unsubscribe());
    });

    private getAttributeItem(code: string, product: Product): NProductViewDetailsLeft.IAtributeItem | undefined {
        const attribute = this.attributeResource.getExisting(code);
        if (!attribute?.data) {
            return undefined;
        }
        const isBoolean = attribute.data.frontend_input === 'boolean';
        const values = product.getAttr<number | string | (number | string)[]>(code);
        if (!values && !isBoolean) {
            return undefined;
        }
        const valuesArray = (Array.isArray(values) ? values : [values]).filter(i => !!i);
        if (!valuesArray.length) {
            return;
        }
        const isText = ['text'].includes(attribute.data.frontend_input || '');
        const isTextArea = ['textarea'].includes(attribute.data.frontend_input || '');
        const valuesLabels = isText || isTextArea ? valuesArray : valuesArray.map(i => attribute.getOptionLabel(i) as string | number);
        return {
            isText,
            isTextArea,
            label: attribute.data.label,
            code,
            valuesLabels
        };
    }

    protected async download(item: IProductAttachment) {
        const url = this.getUrl(item);
        try {
            const response = await fetch(url);
            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = url.split('/').pop() || '';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);

            URL.revokeObjectURL(blobUrl);
        } catch (error) {}
    }

    protected getUrl(item: IProductAttachment): string {
        if (item.file) {
            return this.urlService.toMediaUrl('static/media/productattach/' + item.file);
        }
        return '';
    }

    protected getTechnicalInformation(): [NProductViewDetailsLeft.IAtributeItem[], NProductViewDetailsLeft.IAtributeItem[]] | undefined {
        const items = this.attributes?.map(code => this.getAttributeItem(code, this.product)).filter(i => !!i);
        return (this.technicalInformation = items
            ? [items?.slice(0, Math.ceil(items.length / 2)), items?.slice(Math.ceil(items.length / 2), items.length)]
            : undefined);
    }

    /** Replace all href="#something" with href="/current-path#something" */
    private fixAnchorLinks(html: string): string {
        const path = this.pathname;
        return html.replace(/href="#([^"]+)"/g, `href="${path}#$1"`);
    }

    private get pathname(): string {
        return this.router.url.split('?')[0].split('#')[0];
    }

    /** Sanitized HTML with fixed anchors and preserved `id` */
    private getSafeDescription(): SafeHtml | undefined {
        let raw = this.product.getAttr('description');
        if (!raw) {
            return undefined;
        }
        if (typeof raw !== 'string') {
            raw = String(raw);
        }
        const fixed = this.fixAnchorLinks(raw as string);
        return this.domSanitizer.bypassSecurityTrustHtml(fixed);
    }

    /**
     * `max-height` cannot interpolate to or from `none`, so the pinned pixel height has to
     * paint before the state flips. Same two-step the hide-up component uses.
     */
    protected toggleDescription(): void {
        if (untracked(this.descriptionAnimating)) {
            return;
        }
        this.pinDescriptionHeight();
        this.descriptionAnimating.set(true);
        this.timeoutService.setTimeout(() => {
            this.descriptionExpanded.update(expanded => !expanded);
            this.timeoutService.setTimeout(() => this.descriptionAnimating.set(false), 400);
        }, 50);
    }

    /**
     * Publishes the natural copy height for the transition to animate against. Read fresh on
     * every toggle, because the copy may have reflowed while it was expanded.
     */
    private pinDescriptionHeight(): void {
        const body = this.descriptionBody()?.nativeElement;
        const content = this.descriptionContent()?.nativeElement;
        if (!body || !content) {
            return;
        }
        this.renderer2.setStyle(body, '--product-description-height', `${content.offsetHeight}px`, RendererStyleFlags2.DashCase);
    }

    /**
     * Compares the natural copy height against the collapsed clamp. Only the collapsed
     * state can answer this, because expanding removes the limit being measured against.
     */
    private measureDescription(): void {
        if (untracked(this.descriptionExpanded)) {
            return;
        }
        const body = this.descriptionBody()?.nativeElement;
        const content = this.descriptionContent()?.nativeElement;
        if (!body || !content) {
            return;
        }
        this.pinDescriptionHeight();
        this.descriptionOverflows.set(content.offsetHeight > body.clientHeight + 1);
    }

    /**
     * The clamp height differs per size, so a breakpoint change can flip the verdict
     * without the copy itself resizing. Deferred so the new size class is in effect.
     */
    protected override onBreakpoint(): void {
        super.onBreakpoint();
        this.timeoutService.async(() => this.measureDescription());
    }
}
